<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Leave Balances</h5>
        <p class="page-sub">Real-time leave entitlements per employee</p>
      </div>
      <div class="page-actions">
        <select class="filter-select" v-model="year" @change="fetchBalances">
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
        <div class="ss-wrap" v-click-outside="() => employeeOpen = false">
          <input
            class="ss-input filter-select"
            :class="{ 'ss-input--open': employeeOpen }"
            :value="employeeOpen ? employeeSearch : employeeLabel"
            placeholder="— search employee —"
            @focus="employeeOpen = true; employeeSearch = ''"
            @input="employeeSearch = $event.target.value"
          />
          <button v-if="employeeId" class="ss-clear" type="button" @mousedown.prevent="employeeId = ''; employeeSearch = ''; employeeOpen = false; fetchBalances()">×</button>
          <div v-if="employeeOpen" class="ss-drop">
            <div
              v-for="e in filteredEmployees" :key="e.id"
              class="ss-option"
              :class="{ 'ss-option--active': e.id === employeeId }"
              @mousedown.prevent="employeeId = e.id; employeeOpen = false; employeeSearch = ''; fetchBalances()"
            >
              <span>{{ e.first_name }} {{ e.last_name }}</span>
              <span class="ss-emp-no">{{ e.employee_no }}</span>
            </div>
            <div v-if="!filteredEmployees.length" class="ss-empty">No employees found</div>
          </div>
        </div>
        <div v-if="employeeId && balances.length" class="export-wrap">
          <button class="btn-export" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" />
          </button>
          <div v-if="exportOpen" class="export-menu">
            <button class="export-item" @click="openExportModal('csv'); exportOpen = false">
              <font-awesome-icon icon="file-csv" /> Export CSV
            </button>
            <button class="export-item" @click="openExportModal('pdf'); exportOpen = false">
              <font-awesome-icon icon="file-pdf" /> Export PDF
            </button>
          </div>
        </div>
        <button class="btn-primary-action" @click="accrueBalances" :disabled="accruing">
          <span v-if="accruing" class="spinner-border spinner-border-sm me-1"></span>
          {{ accruing ? 'Accruing…' : 'Run Accrual' }}
        </button>
      </div>
    </div>

    <div v-if="!employeeId" class="empty-state">
      <font-awesome-icon icon="users" class="empty-icon" />
      <p>Select an employee to view their leave balances</p>
    </div>

    <div v-else-if="loading" class="balance-grid">
      <div v-for="n in 4" :key="n" class="sk-card"></div>
    </div>

    <div v-else>
      <!-- Balance Cards -->
      <div class="balance-grid">
        <div v-for="b in balances" :key="b.leave_type_id" class="balance-card">
          <div class="bc-head">
            <div class="bc-icon"><font-awesome-icon icon="umbrella-beach" /></div>
            <div class="bc-title-block">
              <p class="bc-name">{{ b.leave_type?.name || b.leave_type_id }}</p>
              <span class="bc-type-chip" :class="b.leave_type?.is_paid ? 'chip--green' : 'chip--gray'">
                {{ b.leave_type?.is_paid ? 'Paid' : 'Unpaid' }}
              </span>
            </div>
          </div>
          <div class="bc-body">
            <div class="bc-stat">
              <span class="bc-stat-label">Entitled</span>
              <span class="bc-stat-val">{{ b.entitled || 0 }}</span>
            </div>
            <div class="bc-stat">
              <span class="bc-stat-label">Taken</span>
              <span class="bc-stat-val bc-taken">{{ b.taken || 0 }}</span>
            </div>
            <div class="bc-stat">
              <span class="bc-stat-label">Pending</span>
              <span class="bc-stat-val bc-pending">{{ b.pending || 0 }}</span>
            </div>
            <div class="bc-stat">
              <span class="bc-stat-label">Available</span>
              <span class="bc-stat-val bc-avail">{{ b.available || 0 }}</span>
            </div>
          </div>
          <!-- Progress bar: taken / entitled -->
          <div class="bc-progress-wrap">
            <div class="bc-progress-bar">
              <div class="bc-progress-fill" :style="{ width: progressPct(b) + '%' }"></div>
            </div>
            <span class="bc-progress-label">{{ progressPct(b) }}% used</span>
          </div>
          <div v-if="b.carried_forward > 0" class="bc-carried">
            <font-awesome-icon icon="arrow-right" class="carried-icon" />
            {{ b.carried_forward }} day(s) carried forward
          </div>
        </div>
        <div v-if="!balances.length" class="empty-state">
          <p>No balance records for {{ year }}</p>
        </div>
      </div>
    </div>
    <!-- ══ Column picker modal ══ -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-action" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'LeaveBalancesPage',
  directives: {
    clickOutside: {
      mounted(el, binding) { el._clickOutside = e => { if (!el.contains(e.target)) binding.value(e); }; document.addEventListener('mousedown', el._clickOutside); },
      unmounted(el) { document.removeEventListener('mousedown', el._clickOutside); },
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      loading: false,
      accruing: false,
      employeeId: '',
      year: currentYear,
      years: [currentYear - 1, currentYear, currentYear + 1],
      employees: [],
      employeeSearch: '',
      employeeOpen: false,
      balances: [],
      exportOpen:         false,
      showExportModal:    false,
      pendingExportFmt:   'csv',
      selectedExportCols: [],
      exportLoading:      false,
      exportColumnDefs: [
        { key: 'leave_type',  label: 'Leave Type',  pdfWidth: 140 },
        { key: 'type',        label: 'Type',         pdfWidth: 70 },
        { key: 'entitled',    label: 'Entitled',     pdfWidth: 80 },
        { key: 'taken',       label: 'Taken',        pdfWidth: 80 },
        { key: 'pending',     label: 'Pending',      pdfWidth: 80 },
        { key: 'available',   label: 'Available',    pdfWidth: 80 },
        { key: 'carried_fwd', label: 'Carried Fwd',  pdfWidth: 100 },
      ],
    };
  },
  computed: {
    filteredEmployees() {
      const q = (this.employeeSearch || '').toLowerCase();
      if (!q) return this.employees;
      return this.employees.filter(e =>
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
        (e.employee_no || '').toLowerCase().includes(q)
      );
    },
    employeeLabel() {
      if (!this.employeeId) return '';
      const e = this.employees.find(e => e.id === this.employeeId);
      return e ? `${e.first_name} ${e.last_name} (${e.employee_no})` : '';
    },
  },
  mounted() {
    this.fetchEmployees();
  },
  methods: {
    async fetchEmployees() {
      try { 
        const { data } = await axios.get('/payroll/employees/active'); this.employees = data.data || []; 
      } catch (err){
        this.$toast?.error(err.response?.data?.message || 'failed fetching employees');
      }
    },
    async fetchBalances() {
      if (!this.employeeId) return;
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/leave/balances', { params: { employee_id: this.employeeId, year: this.year } });
        this.balances = data.data || [];
      } catch (e) { this.$toast?.error('Failed to load balances'); }
      finally { this.loading = false; }
    },
    async accrueBalances() {
      if (!confirm(`Run monthly accrual for ${this.year}?`)) return;
      this.accruing = true;
      try {
        await axios.post('/hr/leave/accrue', { year: this.year });
        this.$toast?.success('Balances accrued');
        this.fetchBalances();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Accrual failed'); }
      finally { this.accruing = false; }
    },
    progressPct(b) {
      const entitled = b.entitled || 0;
      if (!entitled) return 0;
      return Math.min(100, Math.round(((b.taken || 0) / entitled) * 100));
    },

    openExportModal(fmt) {
      this.pendingExportFmt   = fmt;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getBalanceVal(b, key) {
      switch (key) {
        case 'leave_type':  return b.leave_type?.name || '—';
        case 'type':        return b.leave_type?.is_paid ? 'Paid' : 'Unpaid';
        case 'entitled':    return b.entitled ?? 0;
        case 'taken':       return b.taken ?? 0;
        case 'pending':     return b.pending ?? 0;
        case 'available':   return b.available ?? 0;
        case 'carried_fwd': return b.carried_forward ?? 0;
        default: return '—';
      }
    },
    doExport() {
      const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
      const rows = this.balances;
      if (this.pendingExportFmt === 'csv') {
        const header = cols.map(c => `"${c.label}"`).join(',');
        const body   = rows.map(r => cols.map(c => `"${String(this.getBalanceVal(r, c.key)).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'leave-balances.csv'; a.click(); URL.revokeObjectURL(url);
      } else {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const usable = 761;
        const totalW = cols.reduce((s, c) => s + c.pdfWidth, 0);
        const scale  = usable / totalW;
        const colStyles = {};
        cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
        doc.autoTable({
          head: [cols.map(c => c.label)],
          body: rows.map(r => cols.map(c => this.getBalanceVal(r, c.key))),
          startY: 40, margin: { left: 20, right: 20 }, tableWidth: usable,
          styles: { fontSize: 8, cellPadding: 4, overflow: 'ellipsize' },
          headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold' },
          columnStyles: colStyles,
        });
        doc.save('leave-balances.pdf');
      }
      this.showExportModal = false;
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }
.ss-wrap   { position:relative; min-width:240px; }
.ss-input  { width:100%; height:38px; padding:0 30px 0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; box-sizing:border-box; cursor:pointer; }
.ss-input--open { border-color:#004AAD; }
.ss-clear  { position:absolute; right:8px; top:50%; transform:translateY(-50%); background:none; border:none; color:#7a90b0; font-size:16px; cursor:pointer; line-height:1; padding:0 2px; }
.ss-drop   { position:absolute; top:calc(100% + 4px); left:0; right:0; background:#fff; border:1px solid #dde5f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:300; max-height:220px; overflow-y:auto; }
.ss-option { display:flex; align-items:center; justify-content:space-between; padding:9px 14px; cursor:pointer; font-size:13px; color:#1a2b45; gap:8px; }
.ss-option:hover, .ss-option--active { background:#f0f7ff; color:#004AAD; }
.ss-emp-no { font-size:11px; color:#7a90b0; flex-shrink:0; }
.ss-empty  { padding:10px 14px; font-size:13px; color:#7a90b0; text-align:center; }
.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.me-1 { margin-right:4px; }

.balance-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:16px; }

.balance-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:18px; display:flex; flex-direction:column; gap:14px; transition:box-shadow .15s; }
.balance-card:hover { box-shadow:0 4px 18px rgba(0,0,0,.07); }

.bc-head    { display:flex; align-items:center; gap:12px; }
.bc-icon    { width:42px; height:42px; border-radius:10px; background:#e0efff; color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.bc-title-block { display:flex; flex-direction:column; gap:3px; }
.bc-name    { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }
.bc-type-chip { display:inline-block; font-size:10px; font-weight:700; padding:2px 8px; border-radius:20px; }
.chip--green { background:#dcfce7; color:#16a34a; }
.chip--gray  { background:#f1f5f9; color:#64748b; }

.bc-body    { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.bc-stat    { display:flex; flex-direction:column; gap:2px; }
.bc-stat-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.bc-stat-val   { font-size:18px; font-weight:800; color:#1a2b45; }
.bc-taken   { color:#ea580c; }
.bc-pending { color:#ca8a04; }
.bc-avail   { color:#16a34a; }

.bc-progress-wrap { display:flex; align-items:center; gap:10px; }
.bc-progress-bar  { flex:1; height:6px; background:#f0f4f8; border-radius:99px; overflow:hidden; }
.bc-progress-fill { height:100%; background:#004AAD; border-radius:99px; transition:width .4s; }
.bc-progress-label { font-size:11px; font-weight:600; color:#7a90b0; white-space:nowrap; }

.bc-carried { font-size:12px; color:#7c3aed; display:flex; align-items:center; gap:6px; }
.carried-icon { font-size:10px; }

/* Skeleton */
.sk-card { height:220px; border-radius:14px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-state { grid-column:1/-1; display:flex; flex-direction:column; align-items:center; gap:12px; padding:60px 20px; color:#7a90b0; text-align:center; }
.empty-icon  { font-size:48px; opacity:.3; }

/* ── Export column picker ─────────────────────────────── */
.export-wrap { position:relative; display:inline-flex; }
.btn-export  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-export:hover { background:#f5f7fa; }
.export-menu { position:absolute; top:calc(100% + 4px); right:0; background:#fff; border:1px solid #dde5f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:200; min-width:160px; padding:6px; }
.export-item { display:flex; align-items:center; gap:8px; width:100%; padding:8px 12px; border:none; background:none; font-size:13px; color:#1a2b45; cursor:pointer; border-radius:6px; }
.export-item:hover { background:#f0f7ff; color:#004AAD; }
.btn-outline  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-outline:hover { background:#f5f7fa; }
.col-picker-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
.col-picker-modal  { background:#fff; border-radius:16px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.18); }
.col-picker-header { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 0; }
.col-picker-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub    { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px 6px; flex-shrink:0; }
.col-picker-actions { display:flex; align-items:center; gap:8px; padding:14px 22px 0; }
.col-picker-link   { background:none; border:none; cursor:pointer; color:#004AAD; font-size:12px; font-weight:600; padding:0; }
.col-picker-sep    { color:#dde5f0; }
.col-picker-count  { margin-left:auto; font-size:12px; color:#7a90b0; }
.col-picker-grid   { display:grid; grid-template-columns:1fr 1fr; gap:4px; padding:14px 22px; }
.col-picker-item   { display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:8px; cursor:pointer; border:1px solid #f0f4f8; }
.col-picker-item:hover { background:#f0f7ff; border-color:#c7d8ef; }
.col-picker-check  { cursor:pointer; accent-color:#004AAD; width:15px; height:15px; flex-shrink:0; }
.col-picker-label  { font-size:13px; color:#1a2b45; font-weight:500; }
.col-picker-footer { display:flex; justify-content:flex-end; gap:8px; padding:14px 22px 20px; border-top:1px solid #f0f4f8; margin-top:8px; }
.fade-enter-active,.fade-leave-active { transition:opacity .18s; }
.fade-enter,.fade-leave-to { opacity:0; }
</style>
